import React, { useRef, useCallback } from "react";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import {
  Bold,
  Italic,
  Underline,
  Link,
  List,
  ListOrdered,
  Heading2,
  Heading3,
  Quote,
  Code,
  SeparatorHorizontal,
  Eye
} from "lucide-react";
import DOMPurify from "dompurify";

// Toolbar button component
function ToolbarButton({ icon: Icon, title, onClick, className = "" }) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      title={title}
      onClick={onClick}
      className={`h-8 w-8 p-0 text-slate-400 hover:text-white hover:bg-slate-700 ${className}`}
    >
      <Icon className="h-4 w-4" />
    </Button>
  );
}

export function HtmlContentEditor({ value, onChange, className = "", placeholder = "" }) {
  const textareaRef = useRef(null);
  const [showPreview, setShowPreview] = React.useState(false);

  // Insert HTML tag at cursor position or wrap selected text
  const insertTag = useCallback((openTag, closeTag = "") => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = value.substring(start, end);
    
    let newText;
    let newCursorPos;

    if (selectedText) {
      // Wrap selected text
      newText = value.substring(0, start) + openTag + selectedText + closeTag + value.substring(end);
      newCursorPos = start + openTag.length + selectedText.length + closeTag.length;
    } else {
      // Insert tags at cursor
      newText = value.substring(0, start) + openTag + closeTag + value.substring(end);
      newCursorPos = start + openTag.length;
    }

    onChange({ target: { value: newText } });

    // Restore focus and cursor position
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  }, [value, onChange]);

  // Insert link with prompt
  const insertLink = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = value.substring(start, end) || "текст посилання";

    const url = prompt("Введіть URL посилання:", "https://");
    if (url && url !== "https://") {
      const linkHtml = `<a href="${url}" target="_blank" rel="noopener noreferrer">${selectedText}</a>`;
      const newText = value.substring(0, start) + linkHtml + value.substring(end);
      onChange({ target: { value: newText } });
    }
  }, [value, onChange]);

  // Handle toolbar button click
  const handleToolbarClick = useCallback((actionType) => {
    switch(actionType) {
      case 'bold': insertTag("<b>", "</b>"); break;
      case 'italic': insertTag("<i>", "</i>"); break;
      case 'underline': insertTag("<u>", "</u>"); break;
      case 'h2': insertTag("<h2>", "</h2>"); break;
      case 'h3': insertTag("<h3>", "</h3>"); break;
      case 'link': insertLink(); break;
      case 'quote': insertTag("<blockquote>", "</blockquote>"); break;
      case 'code': insertTag("<code>", "</code>"); break;
      case 'ul': insertTag("<ul>\n  <li>", "</li>\n</ul>"); break;
      case 'ol': insertTag("<ol>\n  <li>", "</li>\n</ol>"); break;
      case 'br': insertTag("<br><br>", ""); break;
      default: break;
    }
  }, [insertTag, insertLink]);

  // Sanitize HTML for preview
  const sanitizedHtml = DOMPurify.sanitize(value || "", {
    ALLOWED_TAGS: ['b', 'i', 'u', 'strong', 'em', 'a', 'br', 'p', 'h1', 'h2', 'h3', 'h4', 'ul', 'ol', 'li', 'blockquote', 'code', 'pre', 'hr', 'span', 'div'],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'class', 'style'],
  });

  return (
    <div className="space-y-2">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-1 p-2 bg-slate-900/60 border border-slate-700 rounded-t-md">
        <ToolbarButton icon={Bold} title="Жирний текст (<b>)" onClick={() => handleToolbarClick('bold')} />
        <ToolbarButton icon={Italic} title="Курсив (<i>)" onClick={() => handleToolbarClick('italic')} />
        <ToolbarButton icon={Underline} title="Підкреслений (<u>)" onClick={() => handleToolbarClick('underline')} />
        <div className="w-px h-6 bg-slate-600 mx-1" />
        <ToolbarButton icon={Heading2} title="Заголовок H2" onClick={() => handleToolbarClick('h2')} />
        <ToolbarButton icon={Heading3} title="Заголовок H3" onClick={() => handleToolbarClick('h3')} />
        <div className="w-px h-6 bg-slate-600 mx-1" />
        <ToolbarButton icon={Link} title="Посилання (<a>)" onClick={() => handleToolbarClick('link')} />
        <ToolbarButton icon={Quote} title="Цитата (<blockquote>)" onClick={() => handleToolbarClick('quote')} />
        <ToolbarButton icon={Code} title="Код (<code>)" onClick={() => handleToolbarClick('code')} />
        <div className="w-px h-6 bg-slate-600 mx-1" />
        <ToolbarButton icon={List} title="Маркований список" onClick={() => handleToolbarClick('ul')} />
        <ToolbarButton icon={ListOrdered} title="Нумерований список" onClick={() => handleToolbarClick('ol')} />
        <div className="w-px h-6 bg-slate-600 mx-1" />
        <ToolbarButton icon={SeparatorHorizontal} title="Новий абзац (<br><br>)" onClick={() => handleToolbarClick('br')} />
        
        <div className="flex-1" />
        <Button
          type="button"
          variant={showPreview ? "default" : "ghost"}
          size="sm"
          onClick={() => setShowPreview(!showPreview)}
          className={`h-8 px-3 text-xs ${showPreview ? 'bg-amber-600 text-white' : 'text-slate-400 hover:text-white hover:bg-slate-700'}`}
        >
          <Eye className="h-4 w-4 mr-1" />
          Перегляд
        </Button>
      </div>

      {showPreview ? (
        /* Preview mode */
        <div 
          className={`min-h-[200px] p-4 bg-white text-slate-800 border border-slate-700 rounded-b-md overflow-auto prose prose-sm max-w-none prose-a:text-amber-700 hover:prose-a:text-amber-500 ${className}`}
          dangerouslySetInnerHTML={{ __html: sanitizedHtml || '<p class="text-slate-400 italic">Попередній перегляд контенту...</p>' }}
        />
      ) : (
        /* Edit mode */
        <Textarea
          ref={textareaRef}
          value={value}
          onChange={onChange}
          placeholder={placeholder || "Введіть контент. Підтримуються HTML-теги: <b>, <i>, <a>, <br>, <ul>, <li> тощо."}
          className={`min-h-[200px] bg-slate-950/60 text-slate-50 border-slate-700 rounded-t-none font-mono text-sm ${className}`}
        />
      )}

      {/* Help text */}
      <p className="text-xs text-slate-500">
        💡 Підтримувані теги: &lt;b&gt;, &lt;i&gt;, &lt;u&gt;, &lt;a href=&quot;...&quot;&gt;, &lt;br&gt;, &lt;h2&gt;, &lt;h3&gt;, &lt;ul&gt;, &lt;ol&gt;, &lt;li&gt;, &lt;blockquote&gt;, &lt;code&gt;
      </p>
    </div>
  );
}
